import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, keys } from '../../api.ts';
import type { CompetitionEligibility, ImportParseResponse, ImportRow, QsoSaveResult } from '../../../shared/types.ts';
import { AdifUpload, ImportPreview } from '../../components/ImportPreview.tsx';
import { ErrorBox, Help, Loading, PageHeader, useDocumentTitle } from '../../components/ui.tsx';
import { RulesSummary, useCompetition } from './CompetitionPage.tsx';

export function ParticipatePage() {
  const id = Number(useParams().id);
  const qc = useQueryClient();
  const comp = useCompetition(id);
  const elig = useQuery({
    queryKey: [...keys.competition(id), 'eligibility'],
    queryFn: () => api.get<CompetitionEligibility>(`/competitions/${id}/eligibility`),
  });
  const [parsed, setParsed] = useState<{ result: ImportParseResponse; name: string } | null>(null);
  const [done, setDone] = useState<QsoSaveResult | null>(null);
  useDocumentTitle(comp.data ? `Osallistu: ${comp.data.competition.heading}` : undefined);

  if (comp.isLoading || elig.isLoading) return <Loading />;
  if (comp.error || !comp.data) return <ErrorBox error={comp.error} />;
  const c = comp.data.competition;

  // Kilpailuajan ulkopuoliset QSO:t näytetään, mutta niitä ei voi valita
  const outside = (r: ImportRow) =>
    r.date < c.startDate ? 'Ennen kilpailun alkua' : r.date > c.endDate ? 'Kilpailun päättymisen jälkeen' : null;

  return (
    <div className="page">
      <PageHeader title={`Osallistu: ${c.heading}`} sub={<RulesSummary c={c} />} actions={<Help name="KilpailuunOsallistuminen" />} />
      {!elig.data?.allowed ? (
        <div className="alert alert-error">Et voi osallistua kilpailuun. {elig.data?.reason}</div>
      ) : (
        <div className="stack">
          {done && (
            <div className="alert alert-ok">
              {done.added} uutta yhteyttä lisätty kilpailuun
              {done.saved > done.added && `, ${done.saved - done.added} oli jo mukana tai toistui tiedostossa`}
              {done.rejected > 0 && `, ${done.rejected} ei täyttänyt kilpailun ehtoja`}.{' '}
              <Link to={`/competitions/${id}`}>Katso tulokset →</Link>
            </div>
          )}
          {!parsed ? (
            <div className="card stack">
              <p className="muted" style={{ margin: 0 }}>
                Lataa kilpailuajan logi ADIF-muodossa (ADIF 2/3, myös LoTW-vienti). Vain kilpailun aikavälille ja säännöille osuvat yhteydet
                hyväksytään.
              </p>
              <AdifUpload
                onParsed={(result, name) => {
                  setDone(null);
                  setParsed({ result, name });
                }}
              />
            </div>
          ) : (
            <div className="card">
              <ImportPreview
                initial={parsed.result.rows}
                fileName={parsed.name}
                target={`/competitions/${id}/entries`}
                rowFilter={outside}
                onDone={(result) => {
                  setDone(result);
                  setParsed(null);
                  void qc.invalidateQueries({ queryKey: keys.competition(id) });
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
